import { ActionService } from '@acme/api';
import { Icon } from '@acme/assets';
import {
  BreadcrumbItem,
  CopyableText,
  formatNumber,
  Page,
  renderFieldIfText,
} from '@acme/foundation';
import {
  $application,
  $contract,
  $participants,
  applicationOpened,
} from '@entities/application';
import { useUnit } from 'effector-react';
import { Button, Card, Label, TextInput } from 'flowbite-react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const TransactionIdPage = () => {
  const { id, actionId } = useParams();
  const navigate = useNavigate();
  const { application, contract, participants, onOpened } = useUnit({
    application: $application,
    contract: $contract,
    participants: $participants,
    onOpened: applicationOpened,
  });

  const [txIds, setTxIds] = useState<string[]>(['']);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clientName =
    participants?.client?.organization?.name ?? contract?.counterparty ?? '—';
  const currencyName =
    (contract?.currency as { name?: string | null } | undefined)?.name ?? '—';

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Главная', onClick: () => navigate('/dashboard') },
    ...(application
      ? [
          {
            label: application.code ?? 'Заявка',
            onClick: () => navigate(`/application/${application.id}`),
          },
          { label: 'Перевод денежных средств' },
        ]
      : [{ label: 'Заявка не найдена' }]),
  ];

  const addTxId = useCallback(() => {
    setTxIds((prev) => [...prev, '']);
  }, []);

  const updateTxId = useCallback((index: number, value: string) => {
    setTxIds((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!actionId || !id) return;
    const ids = txIds.map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) {
      setError('Введите идентификатор TXID');
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const res = await ActionService.postApiV1ActionFormTransactionId({
        id: actionId,
        formData: { txId: ids },
      });
      if (res?.isSuccess) {
        onOpened({ id });
        navigate(`/application/${id}`);
      } else {
        setError(res?.errorText ?? 'Не удалось подтвердить перевод');
      }
    } catch {
      setError('Не удалось подтвердить перевод');
    } finally {
      setIsSubmitting(false);
    }
  }, [actionId, id, txIds, navigate, onOpened]);

  return (
    <Page breadcrumbItems={breadcrumbItems}>
      <div className="flex flex-col gap-6">
        <Card>
          <h2 className="font-semibold text-xl text-gray-900">
            Перевод денежных средств
          </h2>
          <div className="grid grid-cols-2 gap-6 pt-4">
            {renderFieldIfText('Клиент', clientName, {
              link: id ? `/application/${id}` : undefined,
            })}
            {renderFieldIfText('Номер заявки', application?.code ?? undefined)}
            {renderFieldIfText('Валюта перевода', currencyName || undefined)}
            {renderFieldIfText(
              'Сумма к оплате',
              contract?.amount != null
                ? formatNumber(contract.amount)
                : undefined
            )}
          </div>
        </Card>

        <Card>
          <h2 className="font-semibold text-xl text-gray-900">
            Реквизиты для перевода
          </h2>
          <div className="grid grid-cols-2 gap-4 pt-4">
            {renderFieldIfText(
              'Назначение платежа',
              contract?.subjectTransaction ?? undefined
            )}
            <div className="flex flex-col gap-1">
              <div className="text-gray-500 text-sm">Адрес криптокошелька</div>
              <CopyableText
                value={contract?.counterpartyAddress ?? undefined}
                className="min-w-0"
              />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="font-semibold text-xl text-gray-900">
            Подтверждение перевода
          </h2>
          <div className="pt-4 space-y-4 grid grid-cols-2">
            <div>
              {txIds.map((value, index) => (
                <div className="mb-2" key={index}>
                  <Label
                    htmlFor={index === 0 ? 'txid-0' : `txid-${index}`}
                    className="mb-2 block text-sm font-medium text-gray-900"
                  >
                    {index === 0
                      ? 'Идентификатор TXID'
                      : `Идентификатор TXID ${index + 1}`}
                  </Label>
                  <div className="relative">
                    <TextInput
                      id={index === 0 ? 'txid-0' : `txid-${index}`}
                      type="text"
                      placeholder="Введите идентификатор TXID"
                      value={value}
                      onChange={(e) => updateTxId(index, e.target.value)}
                      className="w-full pr-10"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                      <Icon name="file-lines-outline" size={18} />
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addTxId}
                className="flex items-center gap-1 mt-4 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
              >
                <Icon name="plus-outline" size={16} />
                Добавить идентификатор TXID
              </button>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex justify-end pt-2">
                <Button
                  color="purple"
                  onClick={handleSubmit}
                  disabled={
                    !txIds.some((s) => s.trim().length > 0) || isSubmitting
                  }
                >
                  {isSubmitting ? 'Отправка...' : 'Подтвердить перевод'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
};
